// Image intake for branding (logos, advisor photo, signature): any common format → a small, trimmed,
// proportional data URL. SVGs are rasterised (an <img> never runs scripts, and the stored result is a
// bitmap), huge photos are scaled down, and logos lose their empty margins so they align consistently.
export interface NormalizeOptions {
  maxWidth: number; maxHeight: number;
  /** remove transparent / near-white margins (logos, signatures) */
  trim?: boolean;
  /** square crop from the centre (advisor photo) */
  square?: boolean;
  /** make near-white pixels transparent (scanned signatures) */
  whiteToAlpha?: boolean;
}
export const MAX_INPUT_BYTES = 10 * 1024 * 1024;
export const ACCEPT_IMAGES = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif';

export class ImageError extends Error { code: 'too_large' | 'unsupported' | 'unreadable' | 'too_big_after'; constructor(code: ImageError['code']) { super(code); this.code = code; } }

function load(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, fail) => { const im = new Image(); im.onload = () => ok(im); im.onerror = () => fail(new ImageError('unreadable')); im.src = src; });
}

/** Bounding box of the "ink" (non-transparent, not near-white). */
function inkBox(ctx: CanvasRenderingContext2D, w: number, h: number): { x: number; y: number; w: number; h: number } | null {
  const d = ctx.getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = (y * w + x) * 4;
    const a = d[i + 3]!, ink = a > 16 && !(d[i]! > 245 && d[i + 1]! > 245 && d[i + 2]! > 245);
    if (ink) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Read a user-chosen file and return a normalised data URL (WebP when the browser can encode it, else PNG). */
export async function normalizeImage(file: Blob & { type: string; size: number }, o: NormalizeOptions): Promise<{ dataUrl: string; width: number; height: number }> {
  if (file.size > MAX_INPUT_BYTES) throw new ImageError('too_large');
  if (file.type && !/^image\/(png|jpeg|webp|svg\+xml|gif)$/.test(file.type)) throw new ImageError('unsupported');
  const url = URL.createObjectURL(file);
  try {
    const im = await load(url);
    let sw = im.naturalWidth || 1200, sh = im.naturalHeight || 600;
    // rasterise at a generous size first (SVGs have no natural size limit)
    const pre = Math.min(1, 2400 / Math.max(sw, sh));
    sw = Math.max(1, Math.round(sw * pre)); sh = Math.max(1, Math.round(sh * pre));
    const c1 = document.createElement('canvas'); c1.width = sw; c1.height = sh;
    const x1 = c1.getContext('2d', { willReadFrequently: true })!;
    x1.drawImage(im, 0, 0, sw, sh);
    if (o.whiteToAlpha) {
      const img = x1.getImageData(0, 0, sw, sh); const d = img.data;
      for (let i = 0; i < d.length; i += 4) { const m = Math.min(d[i]!, d[i + 1]!, d[i + 2]!); if (m > 200) d[i + 3] = Math.round(d[i + 3]! * Math.max(0, (255 - m) / 55)); }
      x1.putImageData(img, 0, 0);
    }
    let box = { x: 0, y: 0, w: sw, h: sh };
    if (o.trim) { const b = inkBox(x1, sw, sh); if (b) { const pad = Math.round(Math.max(b.w, b.h) * 0.02); box = { x: Math.max(0, b.x - pad), y: Math.max(0, b.y - pad), w: Math.min(sw - Math.max(0, b.x - pad), b.w + 2 * pad), h: Math.min(sh - Math.max(0, b.y - pad), b.h + 2 * pad) }; } }
    if (o.square) { const s = Math.min(box.w, box.h); box = { x: box.x + Math.round((box.w - s) / 2), y: box.y + Math.round((box.h - s) / 2), w: s, h: s }; }
    const k = Math.min(1, o.maxWidth / box.w, o.maxHeight / box.h);
    const w = Math.max(1, Math.round(box.w * k)), h = Math.max(1, Math.round(box.h * k));
    const c2 = document.createElement('canvas'); c2.width = w; c2.height = h;
    const x2 = c2.getContext('2d')!;
    x2.imageSmoothingQuality = 'high';
    x2.drawImage(c1, box.x, box.y, box.w, box.h, 0, 0, w, h);
    let dataUrl = c2.toDataURL('image/webp', 0.92);
    if (!dataUrl.startsWith('data:image/webp')) dataUrl = c2.toDataURL('image/png');
    if (dataUrl.length > 700_000) dataUrl = c2.toDataURL('image/jpeg', 0.85);
    if (dataUrl.length > 700_000) throw new ImageError('too_big_after');
    return { dataUrl, width: w, height: h };
  } finally { URL.revokeObjectURL(url); }
}
